import { useEffect, useState } from "react";
import { useToasts } from "react-toast-notifications";
import ProgressBar from "react-bootstrap/ProgressBar";
import { bool, func, shape } from "prop-types";

import FormModal from "components/modals/Form";
import ModalSummaryText from "components/text/ModalSummaryText";
import ContentUploadForm from "features/sprint/forms/Video";
import { ContentUpload } from "features/sprint/types";
import {
  useCompleteContentMultipartUploadMutation,
  useCreateContentMultipartUploadMutation,
  useUploadContentMultipartPartMutation,
} from "services/projectCity";
import { renameFile } from "utils/general";

function ContentUploadModal({
  contentUpload,
  chooseType,
  chooseInSprint,
  chooseBucket,
  promptChooseCategory,
  followupAction,
  ...props
}) {
  /** Special modal that can go from uploading a content upload to multipart status. */
  const [
    createContentMultipartUpload,
  ] = useCreateContentMultipartUploadMutation();
  const [createContentMultipartPart] = useUploadContentMultipartPartMutation();
  const [
    completeContentMultipartUpload,
  ] = useCompleteContentMultipartUploadMutation();

  // For multipart uploads we'll receive back some data from the form.
  const [createdContentUpload, setCreatedContentUpload] = useState(null);
  const [multipartFile, setMultipartFile] = useState(null);
  const [totalParts, setTotalParts] = useState(null);
  const [uploadedParts, setUploadedParts] = useState(null);
  const { addToast } = useToasts();

  useEffect(() => {
    const uploadObject = createdContentUpload || contentUpload;
    if (uploadObject?.id && multipartFile?.size > 0) {
      setUploadedParts(0);
      uploadMultipart(multipartFile);
    }
  }, [contentUpload, createdContentUpload, multipartFile]);

  const mb = 1048576;
  const partMb = 5;
  const partSize = mb * partMb;
  const isMultipartUpload = uploadedParts !== null;

  async function uploadMultipart(file) {
    /** Full process for uploading a content multipart upload. */

    setTotalParts(Math.ceil(file.size / partSize));
    let startBytes = 0;
    let part;
    let partNumber = 1;

    try {
      const initialResponse = await createContentMultipartUpload({
        contentUpload: createdContentUpload?.id || contentUpload?.id,
        filename: renameFile(file.name),
      });
      if (initialResponse.error) {
        addToast("Oops! There was an error creating your upload.", {
          appearance: "error",
        });
        return props.onHide();
      }

      const { uuid } = initialResponse.data;

      // Now that we've created the multipart instance we can start uploading the chunks.
      while (startBytes < file.size) {
        part = file.slice(
          startBytes,
          Math.min(startBytes + partSize, file.size)
        );
        let formData = new FormData();
        formData.append("part", part);
        formData.append("part_number", partNumber);

        const partResponse = await createContentMultipartPart({
          uuid,
          formData,
        });
        if (partResponse.error) {
          addToast("Oops! There was an error appending parts your file.", {
            appearance: "error",
          });
          return props.onHide();
        }

        setUploadedParts(partNumber);
        startBytes += partSize;
        partNumber += 1;
      }

      // Finish the multipart upload.
      await completeContentMultipartUpload({ uuid });
      addToast("Content upload complete!", { appearance: "success" });
      props.onHide();
    } catch (e) {
      addToast(`An error has occurred.`, {
        appearance: "error",
        autoDismiss: true,
      });
    }
  }

  return (
    <FormModal
      title={isMultipartUpload ? "uploading content..." : "content upload"}
      closeButton={!isMultipartUpload}
      onHide={isMultipartUpload ? () => {} : props.onHide}
      {...props}
    >
      {!isMultipartUpload ? (
        <ContentUploadForm
          closeModal={props.onHide}
          contentUpload={contentUpload}
          setMultipartFile={setMultipartFile}
          setContentUpload={setCreatedContentUpload}
          chooseType={chooseType}
          chooseInSprint={chooseInSprint}
          chooseBucket={chooseBucket}
          promptChooseCategory={promptChooseCategory}
          followupAction={followupAction}
        />
      ) : (
        <>
          <ModalSummaryText className="my-2 text-center">
            Uploading file, do not close...
          </ModalSummaryText>
          <ProgressBar
            now={(uploadedParts / totalParts) * 100}
            variant="info"
            striped
            animated
          />
        </>
      )}
    </FormModal>
  );
}

ContentUploadModal.propTypes = {
  /** Optionally pass in a content upload object to modify. */
  contentUpload: shape(ContentUpload),

  /** Determine if the user should choose type, or if it should be predefined. */
  chooseType: bool,

  /** Determine if the user should choose to share in sprint. */
  chooseInSprint: bool,

  /** Determine if the user should choose the bucket. */
  chooseBucket: bool,

  /** Action to prompt the user to choose a category for their content upload. */
  promptChooseCategory: func,

  /** Action to take after the form has been saved. */
  followupAction: func,
};

ContentUploadModal.defaultProps = {
  contentUpload: null,
  chooseType: null,
  chooseInSprint: false,
  chooseBucket: false,
  promptChooseCategory: () => {},
  followupAction: () => {},
};

export default ContentUploadModal;
